import {StripeElementsConfig, StripeService, StripeServiceCallbacks} from './StripeService';
import {
    createEnhancedPaymentFormHTML,
    createLoadingHTML,
    createPaymentFormHTML,
    getSelectedPaymentMethod as getSelectedPaymentMethodFromDOM,
    injectCustomPaymentStyles,
    injectPaymentStyles,
    setSubmitButtonLoading,
    showPaymentMessage,
    showToast,
    updateSubmitButtonText
} from './UIUtilities';
import {getInitialLocale, getLocalizedDefaultMethodText, PaymentLocale, resolveLocale, t} from './I18nService';
import {
    buildTaxFormPrefillFromConfig,
    CustomPaymentMethod,
    CustomPaymentResponse,
    DEFAULT_CUSTOM_PAYMENT_METHODS,
    PaymentConfig,
    PaymentMethodChangeEvent,
    TaxFormPrefill
} from '../types';
import {ELEMENT_IDS} from './PaymentSDKBase';

export interface PaymentManagerOptions {
    config: PaymentConfig;
    onSuccess?: (paymentIntent: unknown) => void;
    onError?: (error: string) => void;
    onLoading?: (isLoading: boolean) => void;
    containerSelector?: string;
    showToasts?: boolean;
    autoInjectStyles?: boolean;
    isTrial?: boolean | null;
    nextBillingDate?: number | null;
    // Custom payment method callbacks
    onPaymentMethodChange?: (event: PaymentMethodChangeEvent) => void;
}

export interface PaymentFormElements {
    paymentElementSelector: string;
    submitButtonSelector?: string;
    messageContainerSelector?: string;
}

/**
 * Common payment manager that handles the complete payment flow
 * Can be used across Vue, React, and HTML implementations
 */
export class PaymentManager {
    private stripeService: StripeService;
    private options: PaymentManagerOptions;
    private isInitialized = false;
    private isInitializing = false;
    private initializationPromise: Promise<void> | null = null;
    private isLoading = false;
    private customPaymentMethods: CustomPaymentMethod[];
    private usingDefaultCustomMethods: boolean;
    private selectedPaymentMethod: string = 'stripe_js';
    private locale: PaymentLocale;
    private localePromise: Promise<PaymentLocale> | null = null;

    constructor(options: PaymentManagerOptions) {
        this.options = options;
        this.stripeService = new StripeService(options.config);

        // Get available payment methods with initial filtering by payment type
        this.usingDefaultCustomMethods =
            !options.config.customPaymentMethods ||
            options.config.customPaymentMethods === DEFAULT_CUSTOM_PAYMENT_METHODS;
        this.locale = getInitialLocale(options.config);

        let availablePaymentMethods = options.config.customPaymentMethods ?? DEFAULT_CUSTOM_PAYMENT_METHODS;
        availablePaymentMethods = this.filterPaymentMethodsByPaymentType(availablePaymentMethods, options.config.payment_type);

        this.customPaymentMethods = availablePaymentMethods;
        this.selectedPaymentMethod = options.config.defaultPaymentMethod || 'stripe_js';
        this.applyDefaultMethodLocalization();

        if (options.autoInjectStyles !== false) {
            injectPaymentStyles();
            injectCustomPaymentStyles();
        }
    }

    getLocale(): PaymentLocale {
        return this.locale;
    }

    /**
     * Get selected payment method config
     */
    getSelectedPaymentMethod(): CustomPaymentMethod | null {
        if (!this.customPaymentMethods) return null;
        return this.customPaymentMethods.find(m => m.id === this.selectedPaymentMethod) || null;
    }

    /**
     * Initialize the payment manager and fetch required data
     */
    async initialize(): Promise<void> {
        // Return early if already initialized
        if (this.isInitialized) {
            return;
        }

        // If already initializing, return the existing promise
        if (this.isInitializing && this.initializationPromise) {
            return this.initializationPromise;
        }

        // Create and cache the initialization promise
        this.isInitializing = true;
        this.initializationPromise = this.performInitialization();

        try {
            await this.initializationPromise;
        } finally {
            this.isInitializing = false;
        }
    }

    /**
     * Create and mount Stripe elements to the DOM
     */
    async mountElements(elements: PaymentFormElements, customElementsConfig?: Partial<StripeElementsConfig>): Promise<void> {
        if (!this.isInitialized) {
            throw new Error('Payment manager not initialized. Call initialize() first.');
        }

        try {
            // Wait for DOM elements to be available (only required ones)
            const requiredSelectors = [elements.paymentElementSelector];
            // Removed linkElementSelector from required selectors since we're not using it

            await this.stripeService.waitForDOMElements(requiredSelectors);

            // Create and mount elements
            await this.stripeService.createElements(this.options.config);

            // Mount payment element (always required) with custom config if provided
            await this.stripeService.createPaymentElement(
                elements.paymentElementSelector,
                this.options.config,
                customElementsConfig
            );

            // Removed Link Authentication Element creation to eliminate email field completely
            // This prevents any email input fields from appearing in Stripe Elements

            // Setup submit button event listener if provided
            if (elements.submitButtonSelector) {
                this.setupSubmitButton(elements.submitButtonSelector, elements.messageContainerSelector);
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t(this.locale, 'failedToMountStripeElements');
            this.handleError(errorMessage);
            throw error;
        }
    }

    /**
     * Render the complete payment form to a container
     */
    async renderToContainer(container: string | HTMLElement): Promise<void> {
        const containerElement = typeof container === 'string'
            ? document.querySelector(container) as HTMLElement
            : container;

        if (!containerElement) {
            throw new Error(`Container not found: ${container}`);
        }

        // Show loading state immediately with best-effort locale.
        containerElement.innerHTML = createLoadingHTML(undefined, this.locale);

        try {
            await this.ensureLocaleResolved();
            containerElement.innerHTML = createLoadingHTML(undefined, this.locale);

            // Initialize payment manager (only if Stripe is needed)
            const needsStripe = !this.customPaymentMethods ||
                this.customPaymentMethods.some(m => m.requiresStripe);

            if (needsStripe) {
                await this.initialize();
            } else {
                this.isInitialized = true;
            }

            // Render payment form
            let isTrial: boolean = false;
            let nextBillingDate: number = 0;
            let amount: number = 0;
            let beforeDiscountAmount: number = 0;
            let currency: string = '';
            // If StripeService has fetched trial info, prefer that
            if (this.stripeService && typeof this.stripeService.getTrialInfo === 'function') {
                const trialInfo = this.stripeService.getTrialInfo();
                if (typeof trialInfo.isTrial === 'boolean') {
                    isTrial = trialInfo.isTrial;
                }
                if (typeof trialInfo.nextBillingDate === 'number') {
                   nextBillingDate = trialInfo.nextBillingDate;
                }
            }
            if (this.stripeService) {
                const svcFirstCycleAmount = this.stripeService.getFirstCycleAmount();
                const svcAmount = this.stripeService.getAmount();
                const svcBeforeDiscount = this.stripeService.getBeforeDiscountAmount();
                const svcCurrency = this.stripeService.getCurrency();
                const effectiveAmount = isTrial && typeof svcFirstCycleAmount === 'number' && svcFirstCycleAmount > 0
                    ? svcFirstCycleAmount
                    : svcAmount;
                if (typeof effectiveAmount === 'number' && effectiveAmount > 0) {
                    amount = effectiveAmount;
                }
                if (typeof svcBeforeDiscount === 'number' && svcBeforeDiscount > 0) {
                    beforeDiscountAmount = svcBeforeDiscount;
                }
                if (typeof svcCurrency === 'string' && svcCurrency) {
                    currency = svcCurrency;
                }
            }
            // Fallback to options if not found
            if (isTrial == null && typeof this.options.isTrial === 'boolean') {
                isTrial = this.options.isTrial;
            }
            if (nextBillingDate == null && typeof this.options.nextBillingDate === 'number') {
                nextBillingDate = this.options.nextBillingDate;
            }
            if (this.customPaymentMethods && this.customPaymentMethods.length > 0) {
                const showVnTaxForm = (currency || '').toLowerCase() === 'vnd';
                const taxFormPrefill: TaxFormPrefill = showVnTaxForm
                    ? buildTaxFormPrefillFromConfig(this.options.config)
                    : {};
                containerElement.innerHTML = createEnhancedPaymentFormHTML(
                    this.customPaymentMethods,
                    this.selectedPaymentMethod,
                    this.options.config.payment_type,
                    isTrial,
                    nextBillingDate,
                    amount,
                    currency,
                    this.options.config.promotion_code || '',
                    !!this.options.config.promotion_token,
                    !!this.options.config.upgrade,
                    (this.options.config.grant_option === 'voucher' ? 'voucher' : 'auto'),
                    this.options.config.email || '',
                    this.options.config.phone || '',
                    this.locale,
                    showVnTaxForm,
                    taxFormPrefill,
                    beforeDiscountAmount
                );
            } else {
                containerElement.innerHTML = createPaymentFormHTML(this.locale);
            }

            // Setup custom payment method listeners if present
            if (this.customPaymentMethods && this.customPaymentMethods.length > 0) {
                this.setupPaymentMethodListeners();
            }

            // Setup promotion code listeners (Apply / Remove)
            this.setupPromotionListeners(container);

            this.setupTaxFormListeners();

            // Mount Stripe elements only if Stripe is required
            const selectedMethod = this.getSelectedPaymentMethod();
            if (!selectedMethod || selectedMethod.requiresStripe) {
                await this.mountElements({
                    paymentElementSelector: `#${ELEMENT_IDS.paymentElement}`,
                    submitButtonSelector: `#${ELEMENT_IDS.submitButton}`,
                    messageContainerSelector: `#${ELEMENT_IDS.messageContainer}`
                });
            } else {
                // Setup submit button for custom payment methods
                this.setupSubmitButton(`#${ELEMENT_IDS.submitButton}`, `#${ELEMENT_IDS.messageContainer}`);
            }

            // Set initial submit button text based on selected payment method
            updateSubmitButtonText(this.selectedPaymentMethod, undefined, this.locale);

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t(this.locale, 'failedToInitializePayment');
            containerElement.innerHTML = `<div class="elsa-error-message">${errorMessage}</div>`;
            throw error;
        }
    }

    /**
     * Get initialization state
     */
    getIsInitialized(): boolean {
        return this.isInitialized;
    }

    /**
     * Reset the payment manager state
     */
    reset(): void {
        this.stripeService.reset();
        this.isInitialized = false;
        this.isLoading = false;
    }

    /**
     * Cleanup resources
     */
    destroy(): void {
        this.reset();
        // Remove event listeners if needed
        const submitButton = document.querySelector('#submit') as HTMLButtonElement;
        if (submitButton) {
            submitButton.replaceWith(submitButton.cloneNode(true));
        }
    }

    /**
     * Render custom payment methods into a container (for React/Vue)
     */
    renderCustomPaymentMethods(containerId: string = ELEMENT_IDS.customPaymentMethods): void {
        const container = document.getElementById(containerId);
        if (!container || !this.customPaymentMethods || this.customPaymentMethods.length === 0) {
            return;
        }

        // Build payment methods list; inject inline Stripe element right after its option
        let methodsHTML = '';
        const defaultPhone = (this.options.config.phone || '').replace(/"/g, '&quot;');
        const fundiinPhoneHTML = `
            <div id="fundiin-phone-container" class="elsa-fundiin-phone-container" style="display: ${this.selectedPaymentMethod === 'fundiin' ? 'block' : 'none'};">
                <label for="fundiin-phone-input" class="elsa-phone-label">
                    ${t(this.locale, 'phoneNumber')} <span class="elsa-required">*</span>
                </label>
                <input
                    type="tel"
                    id="fundiin-phone-input"
                    name="fundiin-phone"
                    class="elsa-phone-input"
                    placeholder="${t(this.locale, 'enterPhoneNumber')}"
                    value="${defaultPhone}"
                    required />
            </div>
        `;
        this.customPaymentMethods.forEach(method => {
            const isSelected = method.id === this.selectedPaymentMethod;
            const selectedClass = isSelected ? 'elsa-payment-method-selected' : '';
            methodsHTML += `
                <div class="elsa-payment-method-option ${selectedClass}" data-method-id="${method.id}">
                    <input
                        type="radio"
                        id="${ELEMENT_IDS.paymentMethodRadio(method.id)}"
                        name="payment-method"
                        value="${method.id}"
                        ${isSelected ? 'checked' : ''}
                        class="elsa-payment-method-radio" />
                    ${method.icon ? `<span class="elsa-payment-method-icon">${method.icon}</span>` : ''}
                    <div class="elsa-payment-method-wrapper">
                        <label for="${ELEMENT_IDS.paymentMethodRadio(method.id)}" class="elsa-payment-method-content">
                            <span class="elsa-payment-method-label">${method.label}</span>
                            ${method.description ? `<span class="elsa-payment-method-description">${method.description}</span>` : ''}
                            ${method.id === 'fundiin' ? fundiinPhoneHTML : ''}
                        </label>
                    </div>
                </div>
            `;
            if (method.id === 'stripe_js') {
                const display = isSelected ? 'block' : 'none';
                methodsHTML += `
                    <div id="${ELEMENT_IDS.stripeElementContainer}" class="elsa-stripe-element-container-inline" style="display:${display}">
                        <div id="${ELEMENT_IDS.paymentElement}" class="elsa-payment-element"></div>
                    </div>
                `;
            }
        });

        container.innerHTML = methodsHTML;
        container.className = 'elsa-custom-payment-methods';
        this.setupPaymentMethodListeners();
    }

    /**
     * Confirm payment based on selected method
     */
    async confirmPayment(messageContainerSelector?: string): Promise<{
        success: boolean;
        paymentIntent?: unknown;
        error?: string;
        customResponse?: CustomPaymentResponse;
    }> {
        if (this.isLoading) {
            return {success: false, error: t(this.locale, 'processing')};
        }

        const msgSel = messageContainerSelector || `#${ELEMENT_IDS.messageContainer}`;
        const taxErrorEl = document.getElementById(ELEMENT_IDS.taxError);
        if (taxErrorEl) {
            taxErrorEl.textContent = '';
            taxErrorEl.style.display = 'none';
        }

        const currencyLower = (this.stripeService.getCurrency() || '').toLowerCase();
        let desiredTaxJson = '';
        if (currencyLower === 'vnd') {
            const taxCb = document.getElementById(ELEMENT_IDS.taxCheckbox) as HTMLInputElement | null;
            if (taxCb?.checked) {
                const tax_number = (document.getElementById(ELEMENT_IDS.taxNumber) as HTMLInputElement)?.value?.trim() || '';
                const company_name = (document.getElementById(ELEMENT_IDS.taxCompany) as HTMLInputElement)?.value?.trim() || '';
                const buyerRaw = (document.getElementById(ELEMENT_IDS.taxBuyerName) as HTMLInputElement)?.value?.trim() || '';
                const company_address = (document.getElementById(ELEMENT_IDS.taxAddress) as HTMLTextAreaElement)?.value?.trim() || '';
                const invoice_email = (document.getElementById(ELEMENT_IDS.taxEmail) as HTMLInputElement)?.value?.trim() || '';
                if (!tax_number || !company_name || !company_address || !invoice_email) {
                    const errMsg = t(this.locale, 'taxFormRequired');
                    if (taxErrorEl) {
                        taxErrorEl.textContent = errMsg;
                        taxErrorEl.style.display = 'block';
                    }
                    this.handleError(errMsg);
                    showPaymentMessage(errMsg, true, msgSel);
                    return {success: false, error: errMsg};
                }
                const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoice_email);
                if (!emailOk) {
                    const errMsg = t(this.locale, 'taxEmailInvalid');
                    if (taxErrorEl) {
                        taxErrorEl.textContent = errMsg;
                        taxErrorEl.style.display = 'block';
                    }
                    this.handleError(errMsg);
                    showPaymentMessage(errMsg, true, msgSel);
                    return {success: false, error: errMsg};
                }
                this.options.config.tax_info = {
                    tax_number,
                    company_name,
                    company_address,
                    invoice_email,
                    ...(buyerRaw ? {buyer_name: buyerRaw} : {})
                };
                desiredTaxJson = JSON.stringify(this.options.config.tax_info);
            } else {
                delete this.options.config.tax_info;
                desiredTaxJson = '';
            }
        } else {
            delete this.options.config.tax_info;
        }

        const currentMethodEarly = getSelectedPaymentMethodFromDOM() || this.selectedPaymentMethod;
        if (currentMethodEarly === 'stripe_js' && currencyLower === 'vnd' && desiredTaxJson && this.options.config.tax_info) {
            const lastSynced = this.stripeService.getLastCheckoutTaxSynced() ?? '';
            if (desiredTaxJson !== lastSynced) {
                try {
                    await this.stripeService.updateCheckoutTaxInfo(
                        this.options.config,
                        this.options.config.tax_info
                    );
                } catch (syncErr) {
                    const errMsg = syncErr instanceof Error ? syncErr.message : t(this.locale, 'failedToInitializePayment');
                    this.handleError(errMsg);
                    showPaymentMessage(errMsg, true, msgSel);
                    return {success: false, error: errMsg};
                }
            }
        }

        // Validate phone number for Fundiin payment method
        if (this.selectedPaymentMethod === 'fundiin') {
            const phoneInput = document.getElementById('fundiin-phone-input') as HTMLInputElement;
            if (!phoneInput || !phoneInput.value.trim()) {
                const errorMessage = t(this.locale, 'phoneRequiredForFundiin');
                this.handleError(errorMessage);
                if (messageContainerSelector) {
                    showPaymentMessage(errorMessage, true, messageContainerSelector);
                }
                return {success: false, error: errorMessage};
            }
            // Update config with phone number
            this.options.config.phone = phoneInput.value.trim();
        }

        setSubmitButtonLoading(true, undefined, this.locale);
        this.setLoading(true);
        try {
            const currentMethod = getSelectedPaymentMethodFromDOM() || this.selectedPaymentMethod;
            if (currentMethod !== 'stripe_js') {
                await this.stripeService.fetchClientSecret(this.options.config, currentMethod as 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p');
                return {success: true};
            } else {
                const callbacks: StripeServiceCallbacks = {
                    onSuccess: (paymentIntent) => {
                        this.handleSuccess(paymentIntent);
                        if (messageContainerSelector) {
                            showPaymentMessage(t(this.locale, 'paymentSuccessful'), false, messageContainerSelector);
                        }
                        setSubmitButtonLoading(false, undefined, this.locale);
                        this.setLoading(false);
                    },
                    onError: (error) => {
                        const msg = error.message;
                        this.handleError(msg);
                        if (messageContainerSelector) {
                            showPaymentMessage(msg, true, messageContainerSelector);
                        }
                        setSubmitButtonLoading(false, undefined, this.locale);
                        this.setLoading(false);
                    },
                    onLoading: (loading) => {
                        setSubmitButtonLoading(loading, undefined, this.locale);
                        this.setLoading(loading);
                    }
                };
                const result = await this.stripeService.confirmPayment(window.location.href, callbacks);
                return {
                    success: !result.error,
                    error: result.error,
                    paymentIntent: result.data
                };
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t(this.locale, 'failedToInitializePayment');
            this.handleError(errorMessage);
            setSubmitButtonLoading(false, undefined, this.locale);
            this.setLoading(false);
            return {success: false, error: errorMessage};
        } finally {
            // ensure loading cleared if callbacks didn't
            this.setLoading(false);
        }
    }

    /**
     * Apply a user-entered promotion code: re-fetch payment with discount, re-render form.
     */
    async applyPromotionCode(promotionCode: string, container: string | HTMLElement): Promise<void> {
        await this.stripeService.verifyPromotionCode(promotionCode);

        this.options.config.promotion_code = promotionCode;
        this.isInitialized = false;
        this.initializationPromise = null;

        await this.stripeService.refetchWithPromotion(this.options.config);
        await this.stripeService.initializeStripe();
        this.isInitialized = true;

        await this.renderToContainer(container);
    }

    /**
     * Remove currently applied promotion code and re-render form without discount.
     */
    async removePromotionCode(container: string | HTMLElement): Promise<void> {
        delete this.options.config.promotion_code;
        this.isInitialized = false;
        this.initializationPromise = null;

        await this.stripeService.refetchWithPromotion(this.options.config);
        await this.stripeService.initializeStripe();
        this.isInitialized = true;

        await this.renderToContainer(container);
    }

    private setupTaxFormListeners(): void {
        const cb = document.getElementById(ELEMENT_IDS.taxCheckbox) as HTMLInputElement | null;
        const container = document.getElementById(ELEMENT_IDS.taxFormContainer);
        if (!cb || !container) {
            return;
        }
        const fieldIds = [
            ELEMENT_IDS.taxNumber,
            ELEMENT_IDS.taxCompany,
            ELEMENT_IDS.taxBuyerName,
            ELEMENT_IDS.taxAddress,
            ELEMENT_IDS.taxEmail
        ];
        const getFields = (): (HTMLInputElement | HTMLTextAreaElement)[] =>
            fieldIds
                .map(id => document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)
                .filter((el): el is HTMLInputElement | HTMLTextAreaElement => !!el);

        const applyPrefillToFields = (p: TaxFormPrefill): void => {
            const set = (id: string, v?: string): void => {
                const el = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
                if (el && v) {
                    el.value = v;
                }
            };
            set(ELEMENT_IDS.taxNumber, p.tax_number);
            set(ELEMENT_IDS.taxCompany, p.company_name);
            set(ELEMENT_IDS.taxBuyerName, p.buyer_name);
            set(ELEMENT_IDS.taxAddress, p.company_address);
            set(ELEMENT_IDS.taxEmail, p.invoice_email);
        };

        const readPrefillFromDom = (): TaxFormPrefill => {
            const section = cb.closest('.elsa-tax-section');
            const raw = section?.getAttribute('data-tax-prefill');
            if (!raw) {
                return {};
            }
            try {
                return JSON.parse(decodeURIComponent(raw)) as TaxFormPrefill;
            } catch {
                return {};
            }
        };

        const applyToggle = (): void => {
            const on = cb.checked;
            container.style.display = on ? 'block' : 'none';
            const fields = getFields();
            if (on) {
                fields.forEach(el => {
                    el.disabled = false;
                });
                applyPrefillToFields(readPrefillFromDom());
            } else {
                fields.forEach(el => {
                    el.disabled = true;
                    el.value = '';
                });
                this.stripeService.resetCheckoutTaxSync();
            }
            const err = document.getElementById(ELEMENT_IDS.taxError);
            if (err) {
                err.textContent = '';
                err.style.display = 'none';
            }
            delete this.options.config.tax_info;
        };

        cb.addEventListener('change', applyToggle);
        applyToggle();
    }

    private setupPromotionListeners(container: string | HTMLElement): void {
        const applyBtn = document.getElementById(ELEMENT_IDS.promotionApplyBtn);
        const inputEl = document.getElementById(ELEMENT_IDS.promotionInput) as HTMLInputElement | null;

        if (applyBtn && inputEl) {
            applyBtn.addEventListener('click', async () => {
                const code = inputEl.value.trim();
                if (!code) return;
                applyBtn.setAttribute('disabled', 'true');
                applyBtn.textContent = t(this.locale, 'applying');
                try {
                    await this.applyPromotionCode(code, container);
                } catch (err) {
                    const msg = err instanceof Error ? err.message : t(this.locale, 'invalidPromotionCode');
                    showToast(msg, true);
                    applyBtn.removeAttribute('disabled');
                    applyBtn.textContent = t(this.locale, 'apply');
                }
            });
        }

        const removeBtn = document.getElementById(ELEMENT_IDS.promotionRemoveBtn) as HTMLButtonElement | null;
        if (removeBtn) {
            removeBtn.addEventListener('click', async (e) => {
                e.preventDefault();
                if (removeBtn.disabled) return;
                removeBtn.disabled = true;
                removeBtn.textContent = t(this.locale, 'removing');
                try {
                    await this.removePromotionCode(container);
                } catch (err) {
                    const msg = err instanceof Error ? err.message : t(this.locale, 'failedToRemovePromotionCode');
                    showToast(msg, true);
                    removeBtn.disabled = false;
                    removeBtn.textContent = t(this.locale, 'remove');
                }
            });
        }
    }

    private async performInitialization(): Promise<void> {
        try {
            this.setLoading(true);

            // Fetch publishable key and client secret in parallel
            await Promise.all([
                this.ensureLocaleResolved(),
                this.stripeService.fetchPublishableKey(),
                this.stripeService.fetchClientSecret(this.options.config)
            ]);

            // Update payment methods based on currency from API response
            await this.updatePaymentMethodsBasedOnCurrency();

            // Initialize Stripe
            await this.stripeService.initializeStripe();

            this.isInitialized = true;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t(this.locale, 'failedToInitializePayment');
            this.handleError(errorMessage);
            // Clear the promise on error so it can be retried
            this.initializationPromise = null;
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    private async handlePaymentMethodChange(methodId: string): Promise<void> {
        const previousMethod = this.selectedPaymentMethod;
        this.selectedPaymentMethod = methodId;
        const selectedMethod = this.customPaymentMethods.find(m => m.id === methodId);
        if (!selectedMethod) return;
        const shouldShowStripe = selectedMethod.requiresStripe;
        this.toggleStripeElementVisibility(shouldShowStripe);
        this.updatePaymentMethodSelection(methodId);
        updateSubmitButtonText(methodId, undefined, this.locale);

        // Toggle Fundiin phone input visibility
        this.toggleFundiinPhoneInput(methodId === 'fundiin');

        if (shouldShowStripe && this.isInitialized) {
            const paymentElement = document.getElementById(ELEMENT_IDS.paymentElement);
            if (paymentElement && paymentElement.children.length === 0) {
                try {
                    await this.mountElements({
                        paymentElementSelector: `#${ELEMENT_IDS.paymentElement}`,
                        submitButtonSelector: `#${ELEMENT_IDS.submitButton}`,
                        messageContainerSelector: `#${ELEMENT_IDS.messageContainer}`
                    });
                } catch {
                    this.handleError(t(this.locale, 'failedToMountStripeElements'));
                }
            }
        }
        if (this.options.onPaymentMethodChange) {
            this.options.onPaymentMethodChange({
                previousMethod,
                currentMethod: methodId as 'stripe_js' | 'paypal' | 'payoo' | 'momo' | 'shopee' | 'fundiin' | 'my2c2p',
                requiresStripe: selectedMethod.requiresStripe
            });
        }
    }

    /**
     * Update visual selection of payment methods
     */
    private updatePaymentMethodSelection(methodId: string): void {
        const options = document.querySelectorAll('.elsa-payment-method-option');
        options.forEach(option => {
            const optionMethodId = option.getAttribute('data-method-id');
            if (optionMethodId === methodId) {
                option.classList.add('elsa-payment-method-selected');
            } else {
                option.classList.remove('elsa-payment-method-selected');
            }
        });
    }

    /**
     * Setup payment method radio button listeners
     */
    private setupPaymentMethodListeners(): void {
        const radios = document.querySelectorAll('input[name="payment-method"]');
        radios.forEach(radio => {
            radio.addEventListener('change', (event) => {
                const target = event.target as HTMLInputElement;
                if (target.checked) {
                    this.handlePaymentMethodChange(target.value);
                }
            });
        });

        // Also add click listeners to the option containers
        const options = document.querySelectorAll('.elsa-payment-method-option');
        options.forEach(option => {
            option.addEventListener('click', () => {
                const methodId = option.getAttribute('data-method-id');
                if (methodId) {
                    const radio = document.getElementById(ELEMENT_IDS.paymentMethodRadio(methodId)) as HTMLInputElement;
                    if (radio) {
                        radio.checked = true;
                        this.handlePaymentMethodChange(methodId);
                    }
                }
            });
        });
    }

    /**
     * Setup submit button with payment confirmation logic
     */
    private setupSubmitButton(buttonSelector: string, messageContainerSelector?: string): void {
        const submitButton = document.querySelector(buttonSelector) as HTMLButtonElement;
        if (!submitButton) {
            console.warn(`Submit button not found: ${buttonSelector}`);
            return;
        }

        submitButton.addEventListener('click', async (event) => {
            event.preventDefault();
            await this.confirmPayment(messageContainerSelector);
        });
    }

    /**
     * Handle successful payment
     */
    private handleSuccess(paymentIntent: unknown): void {
        if (this.options.showToasts !== false) {
            showToast(t(this.locale, 'paymentSuccessful'), false);
        }
        this.options.onSuccess?.(paymentIntent);
    }

    /**
     * Handle payment error
     */
    private handleError(error: string): void {
        if (this.options.showToasts !== false) {
            showToast(error, true);
        }
        this.options.onError?.(error);
    }

    /**
     * Handle loading state changes
     */
    private setLoading(loading: boolean): void {
        this.isLoading = loading;
        this.options.onLoading?.(loading);
    }

    /**
     * Toggle Stripe element visibility
     */
    private toggleStripeElementVisibility(visible: boolean): void {
        // Hide the entire Stripe element container instead of individual elements
        const stripeContainer = document.getElementById(ELEMENT_IDS.stripeElementContainer);

        if (stripeContainer) {
            stripeContainer.style.display = visible ? 'block' : 'none';
        }
    }

    /**
     * Toggle Fundiin phone input visibility
     */
    private toggleFundiinPhoneInput(visible: boolean): void {
        const phoneContainer = document.getElementById('fundiin-phone-container');
        const phoneInput = document.getElementById('fundiin-phone-input') as HTMLInputElement;

        if (phoneContainer) {
            phoneContainer.style.display = visible ? 'block' : 'none';
        }

        if (phoneInput) {
            phoneInput.required = visible;
        }
    }

    /**
     * Get enabled payment methods based on payment type and currency
     * Defines which payment methods are available for each combination
     * Easy to scale - add new conditions for new currencies or payment types
     */
    private getEnabledPaymentMethods(paymentType: string | undefined, currency: string | null): Set<string> {
        const enabled = new Set<string>();
        enabled.add('stripe_js');

        if (paymentType === 'onetime') {
            enabled.add('paypal');

            if (currency?.toLowerCase() === 'vnd') {
                enabled.add('momo');
                // enabled.add('shopee');
                enabled.add('payoo');
                // enabled.add('fundiin');
            }

            if (['thb', 'idr'].includes(currency?.toLowerCase() || '')) {
                enabled.add('my2c2p');
            }
        }

        return enabled;
    }

    /**
     * Filter payment methods based on enabled methods
     */
    private filterPaymentMethodsByEnabled(methods: CustomPaymentMethod[], enabledMethods: Set<string>): CustomPaymentMethod[] {
        return methods.filter(method => enabledMethods.has(method.id));
    }

    /**
     * Filter payment methods based on payment type
     * Show custom payment methods (payoo, momo, shopee, my2c2p, fundiin) only for one-time payments
     * Show only Stripe for subscription payments
     */
    private filterPaymentMethodsByPaymentType(methods: CustomPaymentMethod[], paymentType: string | undefined): CustomPaymentMethod[] {
        if (!paymentType) {
            return methods;
        }

        // For subscription payments, only show Stripe
        if (paymentType === 'subscription') {
            return methods.filter(method => method.id === 'stripe_js');
        }

        return methods;
    }

    /**
     * Update custom payment methods after currency is available
     * Uses the centralized getEnabledPaymentMethods to determine which methods to show
     */
    private async updatePaymentMethodsBasedOnCurrency(): Promise<void> {
        if (!this.customPaymentMethods) {
            return;
        }

        const currency = this.stripeService.getCurrency();
        const paymentType = this.options.config.payment_type;

        // Get the enabled payment methods based on payment type and currency
        const enabledMethods = this.getEnabledPaymentMethods(paymentType, currency);

        // Filter the available methods to only show enabled ones
        const filteredMethods = this.filterPaymentMethodsByEnabled(this.customPaymentMethods, enabledMethods);

        // Update the payment methods if they changed
        if (filteredMethods.length !== this.customPaymentMethods.length) {
            this.customPaymentMethods = filteredMethods;

            // If the selected method was filtered out, switch to Stripe
            if (!filteredMethods.find(m => m.id === this.selectedPaymentMethod)) {
                this.selectedPaymentMethod = 'stripe_js';
            }
        }
    }

    private applyDefaultMethodLocalization(): void {
        if (!this.usingDefaultCustomMethods) {
            return;
        }
        this.customPaymentMethods = this.customPaymentMethods.map((method) => ({
            ...method,
            ...getLocalizedDefaultMethodText(method.id, this.locale)
        }));
    }

    private async ensureLocaleResolved(): Promise<PaymentLocale> {
        if (this.localePromise) {
            return this.localePromise;
        }
        this.localePromise = resolveLocale(this.options.config).then((locale) => {
            this.locale = locale;
            this.options.config.locale = locale;
            this.applyDefaultMethodLocalization();
            return locale;
        }).catch(() => this.locale);

        return this.localePromise;
    }
}
